import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import * as FaIcons from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import { SocketContext } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { FocusableSection, FocusableButton, FocusableCard, useBackHandler, setFocus } from '../components/Focusable';
import { API_URL } from '../config';
import { QRCodeSVG } from 'qrcode.react';

const Container = styled.div`
  padding: 2rem;
  padding-left: ${props => props.$sidebar ? 'calc(150px + 2rem)' : '90px'};
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1d24 0%, #15171c 100%);
  transition: padding-left 350ms;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 70px;
    padding-left: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const BackButton = styled.button`
  background: rgba(99, 44, 228, 0.2);
  border: 1px solid #632ce4;
  color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(99, 44, 228, 0.4);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }
`;

const Title = styled.h1`
  color: #fff;
  font-size: 2rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 8px;
  }
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #fff;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
`;

const UserEmail = styled.div`
  color: #888;
  font-size: 0.9rem;
`;

const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const GroupCard = styled.div`
  background: ${props => props.$selected ? 'rgba(99, 44, 228, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
  border-radius: 8px;
  padding: 1.25rem;
  cursor: pointer;
  border: 2px solid ${props => props.$selected ? '#632ce4' : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    background: rgba(99, 44, 228, 0.2);
  }
`;

const GroupName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const GroupMeta = styled.div`
  color: #888;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const OwnerBadge = styled.span`
  background: linear-gradient(135deg, #9d50bb, #632ce4);
  color: #fff;
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  text-transform: uppercase;
`;

const SeniorBadge = styled.span`
  background: linear-gradient(135deg, #f6a623, #e07b00);
  color: #fff;
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  text-transform: uppercase;
  margin-left: 0.5rem;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  border: none;
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);
  }
`;

const GroupDetail = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  border: 1px solid rgba(99, 44, 228, 0.3);
`;

const GroupDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const GroupDetailTitle = styled.h3`
  color: #fff;
  margin: 0;
  font-size: 1.2rem;
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const MemberItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const MemberAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #fff;
`;

const MemberName = styled.div`
  color: #fff;
  font-weight: 500;
`;

const MemberEmail = styled.div`
  color: #888;
  font-size: 0.8rem;
`;

const OnlineStatus = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$online ? '#27ae60' : '#888'};
  display: inline-block;
  margin-right: 0.5rem;
`;

const ActionButton = styled.button`
  background: ${props => props.$danger ? 'rgba(231, 76, 60, 0.2)' : 'rgba(99, 44, 228, 0.2)'};
  border: 1px solid ${props => props.$danger ? '#e74c3c' : '#632ce4'};
  color: ${props => props.$danger ? '#e74c3c' : '#fff'};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.$danger ? 'rgba(231, 76, 60, 0.4)' : 'rgba(99, 44, 228, 0.4)'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #1a1d24;
  border-radius: 12px;
  padding: 2rem;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(99, 44, 228, 0.3);

  @media (max-width: 768px) {
    padding: 1.5rem;
    width: 95%;
    border-radius: 8px;
  }
`;

const ModalTitle = styled.h3`
  color: #fff;
  margin: 0 0 1.5rem 0;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 1rem;

  &::placeholder {
    color: #666;
  }

  &:focus {
    outline: none;
    border-color: #632ce4;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  &.primary {
    background: linear-gradient(135deg, #632ce4, #9d50bb);
    color: #fff;
  }

  &.secondary {
    background: transparent;
    border: 1px solid #666;
    color: #fff;
  }

  &.danger {
    background: rgba(231, 76, 60, 0.2);
    border: 1px solid #e74c3c;
    color: #e74c3c;
  }
`;

const EmptyState = styled.div`
  color: #888;
  text-align: center;
  padding: 2rem;
  font-style: italic;
`;

const QrContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`;

const QrWrapper = styled.div`
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QrHint = styled.p`
  color: #888;
  font-size: 0.85rem;
  text-align: center;
  margin: 0;
  line-height: 1.4;
`;

const QrActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const LogoutButton = styled.button`
  width: 100%;
  color: #e74c3c;
  padding: 0.875rem;
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(231, 76, 60, 0.15);
    border-color: #e74c3c;
  }
`;

const Account = ({ sidebar, setCurrentPage, toggleSidebar }) => {
  const { user, logout } = useContext(AuthContext);
  const { onlineUsers } = useContext(SocketContext);
  const toast = useToast();

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [qrToken, setQrToken] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const qrRef = useRef(null);
  const [showResetLinkModal, setShowResetLinkModal] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [resetTargetName, setResetTargetName] = useState('');

  useBackHandler(() => {
    if (showResetLinkModal) {
      setShowResetLinkModal(false);
    } else if (showCreateModal) {
      setShowCreateModal(false);
      setNewGroupName('');
    } else if (showAddMemberModal) {
      setShowAddMemberModal(false);
      setNewMemberEmail('');
    } else if (showConfirmModal) {
      setShowConfirmModal(false);
    } else if (selectedGroup) {
      setSelectedGroup(null);
    }
  });

  const handleBack = () => {
    if (selectedGroup) {
      setSelectedGroup(null);
    } else {
      setCurrentPage('home');
      if (!sidebar && toggleSidebar) {
        toggleSidebar();
      }
    }
  };

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadGroupMembers(selectedGroup.id);
    }
  }, [selectedGroup]);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(res.data);
    } catch (error) {
      console.error('Erreur chargement groupes:', error);
      toast.error('Erreur lors du chargement des groupes');
    } finally {
      setLoading(false);
    }
  };

  const loadGroupMembers = async (groupId) => {
    try {
      const res = await axios.get(`${API_URL}/api/groups/${groupId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupMembers(res.data);
    } catch (error) {
      console.error('Erreur chargement membres:', error);
      toast.error('Erreur lors du chargement des membres');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Nom du groupe requis');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/groups`,
        { name: newGroupName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Groupe cree');
      setShowCreateModal(false);
      setNewGroupName('');
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la creation');
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      toast.error('Email requis');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/groups/${selectedGroup.id}/members`,
        { email: newMemberEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Membre ajoute');
      setShowAddMemberModal(false);
      setNewMemberEmail('');
      loadGroupMembers(selectedGroup.id);
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout');
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await axios.post(
        `${API_URL}/api/groups/${selectedGroup.id}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vous avez quitte le groupe');
      setSelectedGroup(null);
      setShowConfirmModal(false);
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/groups/${selectedGroup.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Groupe supprime');
      setSelectedGroup(null);
      setShowConfirmModal(false);
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(
        `${API_URL}/api/groups/${selectedGroup.id}/members/${memberId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Membre retire');
      loadGroupMembers(selectedGroup.id);
      loadGroups();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleToggleSenior = async (member) => {
    const newValue = !member.is_senior;
    try {
      await axios.put(
        `${API_URL}/api/groups/${selectedGroup.id}/members/${member.id}/senior`,
        { is_senior: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        newValue
          ? `${member.prenom} tague comme senior`
          : `Tag senior retire pour ${member.prenom}`
      );
      loadGroupMembers(selectedGroup.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const openConfirmModal = (action) => {
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewGroupName('');
  };

  const closeAddMemberModal = () => {
    setShowAddMemberModal(false);
    setNewMemberEmail('');
  };

  const handleGenerateQr = async () => {
    setQrLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/qr-token/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrToken(res.data.qr_token);
      toast.success('QR Code genere');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la generation');
    } finally {
      setQrLoading(false);
    }
  };

  const handleGenerateResetLink = async (member) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/password-reset/generate`,
        { targetUserId: member.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const link = `https://famibox.cazapp.fr?reset=${res.data.reset_token}`;
      setResetLink(link);
      setResetTargetName(`${member.prenom} ${member.nom}`);
      setShowResetLinkModal(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la generation du lien');
    }
  };

  const handleCopyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      toast.success('Lien copie dans le presse-papiers');
    } catch {
      // Fallback pour les navigateurs sans clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = resetLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Lien copie dans le presse-papiers');
    }
  };

  const handleDownloadQr = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement('a');
      link.download = `famibox-qr-${user?.prenom || 'code'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [user?.prenom]);

  const isOwner = selectedGroup?.role === 'owner';

  return (
    <FocusableSection focusKey="ACCOUNT_PAGE">
      <Container $sidebar={sidebar}>
        <Header>
          <FocusableButton
            as={BackButton}
            onPress={handleBack}
          >
            <FaIcons.FaArrowLeft /> {selectedGroup ? 'Retour' : 'Retour'}
          </FocusableButton>
          <Title>{selectedGroup ? selectedGroup.name : 'Mon Compte'}</Title>
        </Header>

        {!selectedGroup ? (
          <>
            {/* Mes informations */}
            <Section>
              <SectionTitle>
                <FaIcons.FaUser /> Mes informations
              </SectionTitle>
              <UserCard>
                <Avatar>
                  {user?.prenom?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
                <UserInfo>
                  <UserName>{user?.prenom} {user?.nom}</UserName>
                  <UserEmail>{user?.email}</UserEmail>
                </UserInfo>
              </UserCard>
            </Section>

            {/* QR Code de connexion */}
            <Section>
              <SectionTitle>
                <FaIcons.FaQrcode /> QR Code de connexion
              </SectionTitle>
              {qrToken ? (
                <QrContainer>
                  <QrWrapper ref={qrRef}>
                    <QRCodeSVG value={qrToken} size={200} level="M" />
                  </QrWrapper>
                  <QrHint>
                    Ce QR Code est a usage unique. Scannez-le sur le boitier pour vous connecter.
                    Une fois utilise, il faudra en generer un nouveau.
                  </QrHint>
                  <QrActions>
                    <FocusableButton
                      as={ActionButton}
                      onPress={handleDownloadQr}
                    >
                      <FaIcons.FaDownload /> Telecharger
                    </FocusableButton>
                    <FocusableButton
                      as={ActionButton}
                      onPress={handleGenerateQr}
                    >
                      <FaIcons.FaRedo /> Regenerer
                    </FocusableButton>
                  </QrActions>
                </QrContainer>
              ) : (
                <QrContainer>
                  <QrHint>
                    Generez un QR Code pour vous connecter facilement sur le boitier Famibox,
                    sans avoir a saisir email et mot de passe.
                  </QrHint>
                  <FocusableButton
                    as={AddButton}
                    onPress={handleGenerateQr}
                    disabled={qrLoading}
                  >
                    <FaIcons.FaQrcode /> {qrLoading ? 'Generation...' : 'Generer un QR Code'}
                  </FocusableButton>
                </QrContainer>
              )}
            </Section>

            {/* Mes groupes */}
            <Section>
              <SectionTitle>
                <FaIcons.FaUsers /> Mes groupes
              </SectionTitle>
              {loading ? (
                <EmptyState>Chargement...</EmptyState>
              ) : groups.length === 0 ? (
                <EmptyState>Vous n'appartenez a aucun groupe</EmptyState>
              ) : (
                <GroupGrid>
                  {groups.map(group => (
                    <FocusableCard key={group.id} onPress={() => setSelectedGroup(group)}>
                      <GroupCard $selected={selectedGroup?.id === group.id}>
                        <GroupName>
                          {group.name}
                          {group.role === 'owner' && <OwnerBadge>Createur</OwnerBadge>}
                        </GroupName>
                        <GroupMeta>
                          <FaIcons.FaUserFriends /> {group.member_count} membre{group.member_count > 1 ? 's' : ''}
                        </GroupMeta>
                      </GroupCard>
                    </FocusableCard>
                  ))}
                </GroupGrid>
              )}
              <FocusableButton
                as={AddButton}
                onPress={() => setShowCreateModal(true)}
              >
                <FaIcons.FaPlus /> Creer un groupe
              </FocusableButton>
            </Section>

            {/* Deconnexion */}
            <Section>
              <FocusableButton
                as={LogoutButton}
                onPress={() => {
                  logout();
                  setCurrentPage('login');
                }}
              >
                <FaIcons.FaSignOutAlt /> Se deconnecter
              </FocusableButton>
            </Section>
          </>
        ) : (
          /* Detail du groupe selectionne */
          <Section>
            <SectionTitle>
              <FaIcons.FaUsers /> Membres du groupe
            </SectionTitle>
            <MemberList>
              {groupMembers.map(member => (
                <MemberItem key={member.id}>
                  <MemberInfo>
                    <MemberAvatar>
                      {member.prenom?.charAt(0)?.toUpperCase() || '?'}
                    </MemberAvatar>
                    <div>
                      <MemberName>
                        <OnlineStatus $online={onlineUsers?.includes(member.email)} />
                        {member.prenom} {member.nom}
                        {member.role === 'owner' && <OwnerBadge style={{ marginLeft: '0.5rem' }}>Createur</OwnerBadge>}
                        {member.is_senior && <SeniorBadge>Senior</SeniorBadge>}
                        {member.id === user?.id && <span style={{ color: '#888', marginLeft: '0.5rem' }}>(vous)</span>}
                      </MemberName>
                      <MemberEmail>{member.email}</MemberEmail>
                    </div>
                  </MemberInfo>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {member.id !== user?.id && (
                      <ActionButton onClick={() => handleGenerateResetLink(member)}>
                        <FaIcons.FaKey /> Reinitialiser le mot de passe
                      </ActionButton>
                    )}
                    {isOwner && member.id !== user?.id && (
                      <ActionButton onClick={() => handleToggleSenior(member)}>
                        <FaIcons.FaUserClock /> {member.is_senior ? 'Retirer tag senior' : 'Tag senior'}
                      </ActionButton>
                    )}
                    {isOwner && member.id !== user?.id && member.role !== 'owner' && (
                      <ActionButton $danger onClick={() => handleRemoveMember(member.id)}>
                        <FaIcons.FaTimes /> Retirer
                      </ActionButton>
                    )}
                  </div>
                </MemberItem>
              ))}
            </MemberList>

            <ButtonGroup>
              <FocusableButton
                as={AddButton}
                onPress={() => setShowAddMemberModal(true)}
              >
                <FaIcons.FaUserPlus /> Ajouter un membre
              </FocusableButton>

              {isOwner ? (
                <FocusableButton
                  as={ActionButton}
                  $danger
                  onPress={() => openConfirmModal('delete')}
                >
                  <FaIcons.FaTrash /> Supprimer le groupe
                </FocusableButton>
              ) : (
                <FocusableButton
                  as={ActionButton}
                  $danger
                  onPress={() => openConfirmModal('leave')}
                >
                  <FaIcons.FaSignOutAlt /> Quitter le groupe
                </FocusableButton>
              )}
            </ButtonGroup>
          </Section>
        )}

        {/* Modal creation groupe */}
        {showCreateModal && (
          <Modal onClick={closeCreateModal}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Creer un groupe</ModalTitle>
              <form onSubmit={(e) => { e.preventDefault(); handleCreateGroup(); }}>
                <Input
                  type="text"
                  placeholder="Nom du groupe (ex: Famille, Amis...)"
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreateGroup();
                    }
                  }}
                  autoFocus
                />
                <ModalButtons>
                  <ModalButton type="button" className="secondary" onClick={closeCreateModal}>
                    Annuler
                  </ModalButton>
                  <ModalButton type="submit" className="primary">
                    Creer
                  </ModalButton>
                </ModalButtons>
              </form>
            </ModalContent>
          </Modal>
        )}

        {/* Modal ajout membre */}
        {showAddMemberModal && (
          <Modal onClick={closeAddMemberModal}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Ajouter un membre</ModalTitle>
              <form onSubmit={(e) => { e.preventDefault(); handleAddMember(); }}>
                <Input
                  type="email"
                  placeholder="Adresse email du membre"
                  value={newMemberEmail}
                  onChange={e => setNewMemberEmail(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddMember();
                    }
                  }}
                  autoFocus
                />
                <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                  La personne doit avoir un compte Famibox
                </p>
                <ModalButtons>
                  <ModalButton type="button" className="secondary" onClick={closeAddMemberModal}>
                    Annuler
                  </ModalButton>
                  <ModalButton type="submit" className="primary">
                    Ajouter
                  </ModalButton>
                </ModalButtons>
              </form>
            </ModalContent>
          </Modal>
        )}

        {/* Modal lien de reinitialisation */}
        {showResetLinkModal && (
          <Modal onClick={() => setShowResetLinkModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>Lien de reinitialisation</ModalTitle>
              <p style={{ color: '#ccc', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Envoyez ce lien a {resetTargetName} pour reinitialiser son mot de passe. Le lien est valable 24h et a usage unique.
              </p>
              <Input
                value={resetLink}
                readOnly
                onClick={(e) => e.target.select()}
                style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}
              />
              <ModalButtons>
                <ModalButton className="secondary" onClick={() => setShowResetLinkModal(false)}>
                  Fermer
                </ModalButton>
                <ModalButton className="primary" onClick={handleCopyResetLink}>
                  Copier le lien
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}

        {/* Modal confirmation */}
        {showConfirmModal && (
          <Modal onClick={() => setShowConfirmModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>
                {confirmAction === 'delete' ? 'Supprimer le groupe ?' : 'Quitter le groupe ?'}
              </ModalTitle>
              <p style={{ color: '#ccc', marginBottom: '1.5rem' }}>
                {confirmAction === 'delete'
                  ? 'Cette action est irreversible. Tous les membres seront retires du groupe.'
                  : 'Vous ne pourrez plus communiquer avec les membres de ce groupe.'
                }
              </p>
              <ModalButtons>
                <ModalButton className="secondary" onClick={() => setShowConfirmModal(false)}>
                  Annuler
                </ModalButton>
                <ModalButton
                  className="danger"
                  onClick={confirmAction === 'delete' ? handleDeleteGroup : handleLeaveGroup}
                >
                  {confirmAction === 'delete' ? 'Supprimer' : 'Quitter'}
                </ModalButton>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </FocusableSection>
  );
};

export default Account;
